import React, { useState, useEffect, useCallback, useRef, useContext } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  CardActions,
  Checkbox,
  IconButton,
  CircularProgress,
  Tooltip,
  Divider,
  TextField,
} from "@mui/material";
import { Refresh, Close, Sort, Search } from "@mui/icons-material";
import { BrowserView, isBrowser, isMobile, MobileView } from "react-device-detect";
import { toUTC } from "../../utils/date.js";
import { useProject } from "../../hook/useProject.js";
import { useUser } from "../../hook/useUser.js";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { downloadSitePhotosApi, getSitePhotosApi, uploadSitePhotoApi, renameSitePhotoApi } from "../../api/sitePhoto.js";
import { NotificationContext } from "../../component/popup/NotificationProvider";
import dayjs from "dayjs";
import SelectComponent from "../../component/SelectComponent.js";
import Container from "../../component/Container.js";
import DatePicker from "../../component/DatePicker.js";
import UploadImageButton from "../../component/button/UploadImageButton.js";
import ScrollableBox from "../../component/ScrollableBox.js";
import Popup from "../../component/popup/Popup.js";
import UploadingPopup from "../../component/popup/UploadingPopup.js";
import OptionsPopup from "../../component/popup/OptionsPopup.js";
import ConfirmPopup from "../../component/popup/ConfirmPopup.js";

const SitePhotoPage = ({ isPopup, imgInforValue, closePopup }) => {
  const { projectId } = useParams();
  const { showSuccess, showError } = useContext(NotificationContext);
  const project = useProject(projectId);
  const { user } = useUser();

  const [state, setState] = useState({
    membersOptions: [],
    isLoading: true,
    isUploading: false,
    isSelecting: isBrowser,
    sortAscending: true,
    viewImgPopup: false,
    selectedImage: "",
    selectedName: "",
    selectedId: "",
    selectedItems: [],
    imageList: [],
    notificationOpen: false,
  });

  const [imgFilterState, setImgFilterState] = useState({
    startDate: null,
    endDate: null,
    uploadedBy: [],
    page: 0,
  });

  const showOptionsRef = useRef(false); // for headerRightButtonRef onclick event to reference latest showOptions state
  const { headerRightButtonRef } = useOutletContext();
  const [showOptions, setShowOptions] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [editingItemId, setEditingItemId] = useState(null);
  const [editedName, setEditedName] = useState("");
  const [isConfirming, setIsConfirming] = useState(false);
  const [showRenamePopup, setShowRenamePopup] = useState(false); //mobile

  headerRightButtonRef.current = {
    icon: "assets/icons/menu.svg",
    onClick: () => {
      showOptionsRef.current = !showOptionsRef.current;
      setShowOptions(showOptionsRef.current);
    },
  };

  useEffect(() => {
    if (project && user) {
      const members = user.userRole === "Contractor" ? project.members.filter((m) => m.company === user.company) : project.members;
      setState((prevState) => ({
        ...prevState,
        membersOptions: members.map((m) => ({
          label: `${m.firstName} ${m.lastName}`,
          value: m.userId,
        })),
      }));
      setImgFilterState((prevState) => ({
        ...prevState,
        uploadedBy: members.map((m) => m.userId),
      }));
      getSitePhotos(members.map((m) => m.userId));
    }
  }, [project, user]);

  useEffect(() => {
    if (imgFilterState.uploadedBy.length && imgFilterState.page) {
      getSitePhotos();
    }
  }, [imgFilterState.page]);

  const getSitePhotos = async (uploadedBy, reset) => {
    setState((prevState) => ({
      ...prevState,
      isLoading: reset || state.imageList.length === 0,
    }));
    try {
      const result = await getSitePhotosApi(
        projectId,
        (uploadedBy || imgFilterState.uploadedBy).join(","),
        reset ? 0 : imgFilterState.page,
        imgFilterState.startDate ? toUTC(`${imgFilterState.startDate.format("YYYY/MM/DD")} 00:00:00`).format("YYYY/MM/DD HH:mm:ss") : undefined,
        imgFilterState.endDate ? toUTC(`${imgFilterState.endDate.format("YYYY/MM/DD")} 23:59:59`).format("YYYY/MM/DD HH:mm:ss") : undefined
      );
      if (result?.data) {
        const newImages = result.data?.map((d) => ({
          id: d.sitePhotoId,
          name: d.filename,
          image: d.fileURL,
          date: d.uploadDatetime,
          uploadedBy: `${d.uploadBy.firstName} ${d.uploadBy.lastName}`,
          company: d.uploadBy.company,
        }));

        setState((prevState) => {
          const uniqueImages = newImages.filter(
            (newImage) => !prevState.imageList.some((existingImage) => existingImage.id === newImage.id)
          );
          const updatedImageList = reset ? newImages : [...prevState.imageList, ...uniqueImages];
          const finalSortedImageList = [...updatedImageList].sort((a, b) =>
            prevState.sortAscending
              ? dayjs(a.date).unix() - dayjs(b.date).unix()
              : dayjs(b.date).unix() - dayjs(a.date).unix()
          );
          return {
            ...prevState,
            imageList: finalSortedImageList,
          };
        });

        if (reset) {
          setImgFilterState((prevState) => ({
            ...prevState,
            page: 0,
          }));
        }
      } else {
        showError("Received site photo data occurred error.");
      }
    } catch {
      showError("Received site photo data occurred error.");
    } finally {
      setState((prevState) => ({
        ...prevState,
        isLoading: false,
      }));
    }
  };

  const loadMoreSitePhotos = () => {
    setImgFilterState((prevState) => ({
      ...prevState,
      page: prevState.page + 1,
    }));
  };

  const groupByMonthYear = (list) => {
    return list.reduce((acc, item) => {
      const date = dayjs(item.date);
      const year = date.year();
      const month = date.format("MMM");
      const key = `${month} ${year}`;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(item);
      return acc;
    }, {});
  };
  const sortedImageList = [...state.imageList].sort((a, b) => dayjs(b.date).unix() - dayjs(a.date).unix());
  const groupedData = groupByMonthYear(sortedImageList);

  const handleDateChange = useCallback((field, date) => {
    const dayjsDate = dayjs(date);
    setImgFilterState((prevState) => ({ ...prevState, [field]: dayjsDate }));
  }, []);

  const clickSingleCard = (item) => {
    setState((prevState) => ({
      ...prevState,
      viewImgPopup: true,
      selectedImage: item.image,
      selectedName: item.name,
      selectedId: item.id,
    }));
  };

  const resetButton = () => {
    setImgFilterState({
      startDate: null,
      endDate: null,
      uploadedBy: state.membersOptions.map((m) => m.value),
    });
    getSitePhotos(
      state.membersOptions.map((m) => m.value),
      true
    );
  };

  const uploadImage = async (event) => {
    setState((prevState) => ({
      ...prevState,
      isUploading: true,
    }));

    const files = Array.from(event);
    const uploadPromises = [];

    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);

        const uploadPromise = uploadSitePhotoApi(projectId, formData)
          .then((result) => {
            if (!result) {
              showError(`Failed to upload file: ${file.name}`);
            }
            return { file, success: true };
          })
          .catch((error) => {
            return { file, success: false, error: error.message };
          });
        uploadPromises.push(uploadPromise);
      }
      const results = await Promise.all(uploadPromises);
      const successfulUploads = results.filter((result) => result.success);
      const failedUploads = results.filter((result) => !result.success);

      if (successfulUploads.length > 0) {
        showSuccess(`${successfulUploads.length} New Photo(s) Added.`);
      }

      if (failedUploads.length > 0) {
        showError("Upload failed.");
      }

      getSitePhotos(undefined, true);
    } catch {
      showError("Upload failed.");
    } finally {
      setState((prevState) => ({
        ...prevState,
        isUploading: false,
      }));
    }
  };

  const clickCheckBox = (item) => {
    if (state.selectedItems.find((selected) => selected.id === item.id)) {
      setState((prevState) => ({
        ...prevState,
        selectedItems: state.selectedItems.filter((selected) => selected.id !== item.id),
      }));
    } else {
      setState((prevState) => ({
        ...prevState,
        selectedItems: [...state.selectedItems, item],
      }));
    }
  };

  const downloadImage = async () => {
    const link = document.createElement("a");
    if (state.selectedItems.length === 1) {
      link.href = state.selectedItems[0].image;
      link.download = state.selectedItems[0].name;
      showSuccess("The Site Photo has been successfully downloaded.");
    } else {
      try {
        const response = await downloadSitePhotosApi(projectId, state.selectedItems.map((i) => i.id).join(","));
        if (response?.data) {
          showSuccess("The Site Photo has been successfully downloaded.");
        }
        link.href = response.data;
      } catch {
        showError("Download failed.");
      }
    }
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const reNameImage = async () => {
    try {
      const result = await renameSitePhotoApi(projectId, editingItemId, editedName);
      if (result?.data) {
        showSuccess(`Rename successful.`);
        getSitePhotos(undefined, true);
      }
    } catch {
      showError(`Rename failed.`);
    } finally {
      setIsConfirming(false);
      setEditingItemId(null);
      setShowRenamePopup(false); //mobile
      setState((prevState) => ({
        //mobile
        ...prevState,
        viewImgPopup: false,
      }));
    }
  };

  return (
    <>
      <Box width="100%" height="100%">
        <BrowserView style={{ width: "100%", height: "100%" }}>
          <Container title="Site Photo" icon="/assets/icons/photo.svg" sx={{ height: "100%" }} overflow="hidden">
            <>
              <Box
                sx={{
                  display: "flex",
                  px: 2,
                  pt: 1,
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "end", gap: 2 }}>
                  <Box>
                    <SelectComponent
                      options={state.membersOptions}
                      value={imgFilterState.uploadedBy}
                      onChange={(selectedValues) => {
                        setImgFilterState((prevState) => ({
                          ...prevState,
                          uploadedBy: selectedValues,
                        }));
                      }}
                      selectLabel="Uploaded By"
                      selectStyle={{
                        width: "100%",
                        minWidth: 140,
                        maxWidth: 140,
                      }}
                      placeholder="Select a name..."
                      isMultiSelect
                    />
                  </Box>
                  <Box sx={{ minWidth: 330, maxWidth: 330 }}>
                    <Typography
                      sx={{
                        color: "#333",
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                      }}
                    >
                      Date Range
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 0.5,
                        alignItems: "center",
                        my: 0.5,
                      }}
                    >
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          value={imgFilterState.startDate}
                          onChange={(date) => handleDateChange("startDate", date)}
                          inputStyle={{ height: 26, py: 0 }}
                        />
                        <Typography>to</Typography>
                        <DatePicker
                          value={imgFilterState.endDate}
                          onChange={(date) => handleDateChange("endDate", date)}
                          inputStyle={{ height: 26, py: 0 }}
                        />
                      </LocalizationProvider>
                    </Box>
                  </Box>
                  <Box sx={{ mb: 0.5 }}>
                    <Button
                      onClick={() => {
                        getSitePhotos(undefined, true);
                      }}
                      variant="contained"
                      color="secondary"
                      sx={{ borderRadius: "10px", py: 0.3 }}
                    >
                      Search
                    </Button>
                    <Button onClick={resetButton} variant="outlined" color="inherit" sx={{ borderRadius: "10px", py: 0.2, ml: 1 }}>
                      Reset
                    </Button>
                  </Box>
                </Box>
                {!isPopup && (
                  <Box sx={{ display: "flex", alignItems: "end", gap: 2, pb: 0.5 }}>
                    <Typography component="label">
                      <UploadImageButton onChange={uploadImage} accept=".jpg,.jpeg,.png,.gif,.bmp,.tiff,.webp" multiple maxSizeMB={10}></UploadImageButton>
                    </Typography>
                    <Button
                      onClick={downloadImage}
                      variant="contained"
                      color={state.selectedItems.length ? "secondary" : "primary"}
                      sx={{
                        pr: 1.5,
                        pl: 1,
                        py: 0,
                        lineHeight: "35px",
                        borderRadius: "20px",
                      }}
                      startIcon={
                        <img
                          src={`assets/buttons/${state.selectedItems.length ? "bluedownload" : "download-disabled"}.svg`}
                          alt="Download Selected"
                        />
                      }
                      disabled={!state.selectedItems.length}
                    >
                      Download Selected
                    </Button>
                  </Box>
                )}
              </Box>
              <Box display="flex" justifyContent="center" pt={2} px={2}>
                <Divider sx={{ width: "100%" }} />
              </Box>
              <ScrollableBox
                sx={{
                  pb: 2,
                  height: isPopup ? "calc(100vh - 406px)" : "calc(100% - 110px)",
                  overflow: "auto",
                }}
                onScrollEnd={loadMoreSitePhotos}
              >
                {state.isLoading ? (
                  <Box width="100%" height="100%" display="flex" flexDirection="column" justifyContent="center" alignItems="center">
                    <CircularProgress />
                  </Box>
                ) : state.imageList.length === 0 ? (
                  <Box width="100%" height="100%" display="flex" flexDirection="column" justifyContent="center" alignItems="center">
                    <Typography>No Site Photos</Typography>
                  </Box>
                ) : (
                  Object.entries(groupedData).map(([key, items], index) => (
                    <Box key={index} sx={{ px: 2, mt: index === 0 ? 0 : 4 }}>
                      {index > 0 && (
                        <Box display="flex" justifyContent="center">
                          <Divider sx={{ width: "100%" }} />
                        </Box>
                      )}
                      <Typography variant="h6" sx={{ textAlign: "center", py: 1 }}>
                        {key}
                      </Typography>
                      <Grid container spacing={3}>
                        {items.map((item, idx) => (
                          <Grid item lg={2.4} xs={4} key={idx}>
                            <Card
                              sx={{
                                backgroundColor: "#F8F8F8",
                                cursor: "pointer",
                                transition: "transform 0.2s, box-shadow 0.2s",
                                "&:hover": {
                                  transform: "scale(1.01)",
                                  boxShadow: "0px 8px 16px rgba(0, 0, 0, 0.2)",
                                },
                              }}
                            // onClick={() => clickSingleCard(item)}
                            >
                              <CardActions
                                sx={{
                                  py: 0.5,
                                  display: "flex",
                                  justifyContent: "space-between",
                                }}
                              >
                                {!isPopup && editingItemId === item.id ? (
                                  <TextField
                                    value={editedName}
                                    onChange={(e) => setEditedName(e.target.value)}
                                    onBlur={() => setIsConfirming(true)}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") setIsConfirming(true);
                                    }}
                                    autoFocus
                                    size="small"
                                    sx={{
                                      width: "80%",
                                      "& .MuiInputBase-input": {
                                        p: "4px 10px",
                                      },
                                    }}
                                  />
                                ) : (
                                  <Tooltip title={item.name}>
                                    <Typography
                                      sx={{
                                        fontWeight: 600,
                                        width: "80%",
                                        overflow: "hidden",
                                        whiteSpace: "nowrap",
                                        textOverflow: "ellipsis",
                                        cursor: "pointer",
                                      }}
                                      onDoubleClick={(event) => {
                                        event.stopPropagation();
                                        setEditingItemId(item.id);
                                        setEditedName(item.name);
                                      }}
                                    >
                                      {item.name}
                                    </Typography>
                                  </Tooltip>
                                )}
                                <Checkbox
                                  color="primary"
                                  checked={state.selectedItems.some((selected) => selected.id === item.id)}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    clickCheckBox(item);
                                  }}
                                  sx={{ p: 0 }}
                                />
                              </CardActions>
                              <CardMedia
                                onClick={() => clickSingleCard(item)}
                                image={item.image}
                                title="Image"
                                sx={{ height: 120, mx: 1, borderRadius: "5px" }}
                              />
                              <CardContent
                                onClick={() => clickSingleCard(item)}
                                sx={{
                                  textAlign: "right",
                                  p: "8px!important",
                                  color: "#717273",
                                }}
                              >
                                <Typography fontSize={11}>
                                  {item.uploadedBy} ({item.company}) -- {dayjs(item.date).format("DD MMM YYYY")}
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  ))
                )}
              </ScrollableBox>
              {isPopup && (
                <Box sx={{ textAlign: "right", pr: 3, py: 2 }}>
                  <Button
                    disabled={state.selectedItems.length === 0}
                    onClick={() => {
                      imgInforValue(state.selectedItems);
                    }}
                    variant="contained"
                    color={state.selectedItems.length === 0 ? "primary" : "success"}
                    sx={{ borderRadius: "10px", lineHeight: "normal" }}
                  >
                    Add
                  </Button>
                  <Button onClick={closePopup} variant="outlined" color="inherit" sx={{ borderRadius: "10px", lineHeight: "normal", ml: 2 }}>
                    Back
                  </Button>
                </Box>
              )}
            </>
          </Container>
        </BrowserView>
        <MobileView style={{ height: "100%" }}>
          {showSearch && (
            <Box>
              <SelectComponent
                options={state.membersOptions}
                value={imgFilterState.uploadedBy}
                onChange={(selectedValues) => {
                  setImgFilterState((prevState) => ({
                    ...prevState,
                    uploadedBy: selectedValues,
                  }));
                }}
                selectLabel="Search - Upload by"
                selectStyle={{ width: "100%" }}
                placeholder="Select a name..."
                isMultiSelect
              />
              <Typography sx={{ color: "#333", fontWeight: 700, whiteSpace: "nowrap" }}>Search - Date Range</Typography>
              <Box
                sx={{
                  display: "flex",
                  gap: 0.5,
                  alignItems: "center",
                  my: 0.5,
                }}
              >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={imgFilterState.startDate}
                    onChange={(date) => handleDateChange("startDate", date)}
                    inputStyle={{ height: 26, py: 0 }}
                  />
                  <Typography>to</Typography>
                  <DatePicker
                    value={imgFilterState.endDate}
                    onChange={(date) => handleDateChange("endDate", date)}
                    inputStyle={{ height: 26, py: 0 }}
                  />
                </LocalizationProvider>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  gap: 0.5,
                  alignItems: "center",
                  my: 0.5,
                }}
              >
                <Button
                  variant="contained"
                  startIcon={<Search />}
                  sx={{ flex: 1, borderRadius: "4px", height: 25 }}
                  onClick={() => {
                    getSitePhotos(undefined, true);
                  }}
                >
                  Search
                </Button>
                <IconButton
                  onClick={resetButton}
                  aria-label="reset"
                  sx={{
                    border: "1px solid #c6ccd2",
                    borderRadius: "4px",
                    padding: "2px 3px",
                    boxSizing: "border-box",
                  }}
                >
                  <Refresh sx={{ fontSize: "20px" }} />
                </IconButton>
                <IconButton
                  onClick={() => {
                    setShowSearch(false);
                  }}
                  aria-label="reset"
                  sx={{
                    border: "1px solid #c6ccd2",
                    borderRadius: "4px",
                    padding: "2px 3px",
                    boxSizing: "border-box",
                  }}
                >
                  <Close sx={{ fontSize: "20px" }} />
                </IconButton>
              </Box>
            </Box>
          )}
          {state.isSelecting ? (
            <Box display="flex" justifyContent="space-between">
              <Button
                sx={{ color: "#717273" }}
                startIcon={<Close />}
                onClick={() => {
                  setState((prevState) => ({
                    ...prevState,
                    isSelecting: false,
                    selectedItems: [],
                  }));
                }}
              >
                {state.selectedItems.length} Selected
              </Button>
            </Box>
          ) : (
            <Box display="flex" justifyContent="space-between">
              <Button
                sx={{ color: "#717273" }}
                startIcon={
                  <Sort
                    sx={{
                      transform: `scaleY(${state.sortAscending ? 1 : -1})`,
                    }}
                  />
                }
                onClick={() => {
                  setState((prevState) => {
                    const newSortAscending = !prevState.sortAscending;
                    const sortedList = [...prevState.imageList].sort((a, b) =>
                      newSortAscending ? dayjs(b.date).unix() - dayjs(a.date).unix() : dayjs(a.date).unix() - dayjs(b.date).unix()
                    );

                    return {
                      ...prevState,
                      sortAscending: newSortAscending,
                      imageList: sortedList,
                    };
                  });
                }}
              >
                Date Taken
              </Button>
              <Button
                sx={{ color: "#717273" }}
                onClick={() => {
                  setState((prevState) => ({
                    ...prevState,
                    isSelecting: true,
                  }));
                }}
              >
                Select
              </Button>
            </Box>
          )}
          <Box sx={{ height: showSearch ? "calc(100% - 180px)" : "calc(100% - 85px)", overflow: "auto" }}>
            <Grid container spacing={1}>
              {state.imageList.map((item, idx) => (
                <Grid item lg={2.4} xs={4} key={idx}>
                  <Card
                    sx={{
                      border: "unset",
                      boxShadow: "unset",
                      cursor: "pointer",
                      transition: "transform 0.2s, box-shadow 0.2s",
                    }}
                    onClick={() => {
                      state.isSelecting ? clickCheckBox(item) : clickSingleCard(item);
                    }}
                  >
                    {state.isSelecting && (
                      <CardActions
                        sx={{
                          p: 0,
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography></Typography>
                        <Checkbox
                          color="primary"
                          checked={state.selectedItems.some((selected) => selected.id === item.id)}
                          onClick={(event) => {
                            event.stopPropagation();
                            clickCheckBox(item);
                          }}
                          sx={{ p: 0 }}
                        />
                      </CardActions>
                    )}
                    <CardMedia image={item.image} title="Image" sx={{ height: 80, mx: 1 }} />
                    <CardContent
                      sx={{
                        textAlign: "center",
                        pt: 1,
                        pb: "8px!important",
                        color: "#717273",
                      }}
                    >
                      <Typography variant="subtitle1" fontSize={11} lineHeight={1.5}>
                        {item.uploadedBy}
                      </Typography>
                      <Typography variant="subtitle1" fontSize={11} lineHeight={1.5}>
                        ({item.company})
                      </Typography>
                      <Typography variant="subtitle1" fontSize={11} lineHeight={1.5}>
                        {dayjs(item.date).format("DD MMM YYYY")}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </MobileView>
      </Box>
      <Popup
        open={state.viewImgPopup}
        onClose={() => {
          setState((prevState) => ({
            ...prevState,
            viewImgPopup: false,
          }));
        }}
        popupStyle={{
          minWidth: 200,
          maxWidth: 800,
          maxHeight: "calc(100vh - 100px)",
        }}
        title={state.selectedName}
      >
        <Box>
          <MobileView>
            <img
              src="assets/icons/edit.svg"
              alt="tune"
              style={{
                width: 16,
                height: 16,
                position: "absolute",
                right: "32px",
                top: "8px",
              }}
              onClick={() => {
                setShowRenamePopup(true);
                setEditedName(state.selectedName);
                setEditingItemId(state.selectedId);
              }}
            />
          </MobileView>
          <img src={state.selectedImage} alt="Selected" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
        </Box>
      </Popup>
      <Popup
        open={showRenamePopup}
        onClose={() => setShowRenamePopup(false)}
        popupStyle={{
          minWidth: 260,
        }}
        title="Rename Image"
      >
        <Box sx={{ p: 2 }}>
          <TextField value={editedName} onChange={(e) => setEditedName(e.target.value)} size="small" sx={{ width: "100%" }} />
          <Box sx={{ textAlign: "center" }}>
            <Button variant="contained" color="secondary" onClick={reNameImage} sx={{ lineHeight: "normal", mt: 2, mr: 1 }}>
              Confirm
            </Button>
            <Button variant="outlined" color="inherit" onClick={() => setShowRenamePopup(false)} sx={{ lineHeight: "normal", mt: 2 }}>
              Cancel
            </Button>
          </Box>
        </Box>
      </Popup>
      <ConfirmPopup
        open={isConfirming}
        message="Confirm to rename the image?"
        onCancel={() => {
          setIsConfirming(false);
          setEditingItemId(null);
        }}
        onConfirm={reNameImage}
      ></ConfirmPopup>
      <UploadingPopup
        open={state.isUploading}
        message={isMobile ? "Uploading..." : "Uploading site photo..."}
        icon="assets/icons/uploadcloudcircle.svg"
      ></UploadingPopup>
      <OptionsPopup
        open={showOptions}
        options={[
          {
            label: "Download Selected",
            icon: <img src={"assets/icons/download.svg"} alt="download"></img>,
            onClick: downloadImage,
            disabled: !state.selectedItems.length,
          },
        ]}
      ></OptionsPopup>
      {!showSearch && (
        <MobileView>
          <Box
            display="flex"
            sx={{
              position: "fixed",
              left: 0,
              bottom: 8,
              p: 1,
              gap: 1,
              width: "100%",
              justifyContent: "center",
            }}
          >
            <Button
              variant="outlined"
              startIcon={<Search />}
              sx={{
                width: "70vw",
                backgroundColor: "#fff",
                borderRadius: 10,
                color: "#D0D5DE",
                borderColor: "#D0D5DE",
              }}
              onClick={() => {
                setShowSearch(true);
              }}
            >
              Search Photo
            </Button>
            <UploadImageButton onChange={uploadImage} accept=".png,.jpg,.jpeg" multiple maxSizeMB={10}></UploadImageButton>
          </Box>
        </MobileView>
      )}
    </>
  );
};
export default SitePhotoPage;
